import useToastStore from "@lib/stores/toast";
import { useMemo } from "react";

interface ToastOptions {
  type?: string;
  text?: string;
  message?: string;
  duration?: number;
  position?: string;
  animation?: string;
  animationDuration?: number;
}

interface ToastFn {
  (options: ToastOptions | string): void;
  success: (text: string, options?: Partial<ToastOptions>) => void;
  error: (text: string, options?: Partial<ToastOptions>) => void;
  info: (text: string, options?: Partial<ToastOptions>) => void;
  warning: (text: string, options?: Partial<ToastOptions>) => void;
}

const useToast = () => {
  const addToast = useToastStore((state) => state.addToast);

  const toast = useMemo(() => {
    const toastFn = ((options: ToastOptions | string) => {
      const config = typeof options === "string" ? { text: options } : options;

      const {
        type = "info",
        text = "",
        message = "",
        duration = 3000,
        position = "bottom-right",
        animation = "animate-slide-in-up",
        animationDuration = 300,
      } = config;

      addToast({
        type,
        text: text || message,
        duration,
        position,
        animation,
        animationDuration,
      });
    }) as ToastFn;

    toastFn.success = (text, options) => toastFn({ type: "success", text, ...options });
    toastFn.error = (text, options) => toastFn({ type: "error", text, ...options });
    toastFn.info = (text, options) => toastFn({ type: "info", text, ...options });
    toastFn.warning = (text, options) => toastFn({ type: "warning", text, ...options });

    return toastFn;
  }, [addToast]);

  return toast;
};

export { useToast };
export default useToast;
